import { useState, useMemo, useEffect } from "react";
import PageHero from "../components/PageHero";
import { StaggerGroup, StaggerItem } from "../components/Reveal";
import { MapPin, Zap } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { sized } from "../lib/images";

const BESS_IMG =
  "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/fhc0bbvj_BESS.png";
const SOLAR_IMG_A = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/j52wcb1e_Foto%201%20PV%20Plant.png";
const SOLAR_IMG_B = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/q0jcs6xp_PV%20Plant%202.jpg";
const SOLAR_IMG_C = "https://images.pexels.com/photos/16586150/pexels-photo-16586150.jpeg";
const SOLAR_IMG_D = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/u4q9nc59_mountain%20pv%20plant.png";
const SOLAR_IMG_E = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/97mi2hvh_PV%20Plant%203.jpg";
const SOLAR_IMG_F = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/bbowng21_PV%20Plant%204.jpg";
const SOLAR_IMG_G = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/cktsyvia_PV%20Plant%205.jpg";
const SOLAR_IMG_H = "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/b318oxlw_PV%20Plant%206.jpg";

// Imagens genéricas para os projetos BMS (por tipo de edifício)
const BMS_IMG_AIRPORT_A = "https://images.pexels.com/photos/227690/pexels-photo-227690.jpeg";
const BMS_IMG_AIRPORT_B = "https://images.pexels.com/photos/2007401/pexels-photo-2007401.jpeg";
const BMS_IMG_AIRPORT_C = "https://images.pexels.com/photos/358319/pexels-photo-358319.jpeg";
const BMS_IMG_HEALTH_A = "https://images.pexels.com/photos/236380/pexels-photo-236380.jpeg";
const BMS_IMG_HEALTH_B = "https://images.pexels.com/photos/269077/pexels-photo-269077.jpeg";
const BMS_IMG_SCHOOL_A = "https://images.pexels.com/photos/256395/pexels-photo-256395.jpeg";
const BMS_IMG_SCHOOL_B = "https://images.pexels.com/photos/2675061/pexels-photo-2675061.jpeg";
const BMS_IMG_SCHOOL_C = "https://images.pexels.com/photos/289737/pexels-photo-289737.jpeg";
const BMS_IMG_UNI_A = "https://images.pexels.com/photos/356065/pexels-photo-356065.jpeg";
const BMS_IMG_UNI_B = "https://images.pexels.com/photos/2305098/pexels-photo-2305098.jpeg";
const BMS_IMG_LIBRARY = "https://images.pexels.com/photos/590493/pexels-photo-590493.jpeg";
const BMS_IMG_THEATRE = "https://images.pexels.com/photos/269140/pexels-photo-269140.jpeg";
const BMS_IMG_POOL_A = "https://images.pexels.com/photos/260598/pexels-photo-260598.jpeg";
const BMS_IMG_POOL_B = "https://images.pexels.com/photos/1263349/pexels-photo-1263349.jpeg";
const BMS_IMG_POOL_C = "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg";
const BMS_IMG_POOL_D = "https://images.pexels.com/photos/261238/pexels-photo-261238.jpeg";
const BMS_IMG_HOTEL_A = "https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg";
const BMS_IMG_HOTEL_B = "https://images.pexels.com/photos/2096983/pexels-photo-2096983.jpeg";
const BMS_IMG_HOTEL_C = "https://images.pexels.com/photos/189296/pexels-photo-189296.jpeg";
const BMS_IMG_HOTEL_D = "https://images.pexels.com/photos/261169/pexels-photo-261169.jpeg";
const BMS_IMG_HOTEL_E = "https://images.pexels.com/photos/258154/pexels-photo-258154.jpeg";
const BMS_IMG_OFFICE_A = "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg";
const BMS_IMG_OFFICE_B = "https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg";
const BMS_IMG_GYM_A = "https://images.pexels.com/photos/1954524/pexels-photo-1954524.jpeg";
const BMS_IMG_GYM_B = "https://images.pexels.com/photos/260352/pexels-photo-260352.jpeg";
const BMS_IMG_CIVIC = "https://images.pexels.com/photos/1838640/pexels-photo-1838640.jpeg";
const BMS_IMG_MARKET_A = "https://images.pexels.com/photos/2292919/pexels-photo-2292919.jpeg";
const BMS_IMG_MARKET_B = "https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg";
const BMS_IMG_CITY = "https://images.pexels.com/photos/373893/pexels-photo-373893.jpeg";
const BMS_IMG_INDUSTRY = "https://images.pexels.com/photos/236698/pexels-photo-236698.jpeg";

const PROJECTS = [
  // Romania
  { name: "Iepurești", country: "Romania", capacity: "169 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_A },
  { name: "Ghimpați", country: "Romania", capacity: "146 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_E },
  { name: "Satu Mare", country: "Romania", capacity: "69 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_C },
  { name: "Șimleu I", country: "Romania", capacity: "55 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_H },
  { name: "Șimleu II", country: "Romania", capacity: "48 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_D },

  // Portugal
  { name: "Central SCADA", country: "Portugal", capacity: "54 MWp", scope: ["SCADA", "SOLAR"], img: "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/skyadi8j_SCADA.png" },
  { name: "Foral", country: "Portugal", capacity: "43 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_B },
  { name: "Castelo Branco", country: "Portugal", capacity: "37 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_F },
  { name: "Rio Maior", country: "Portugal", capacity: "30 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_G },
  { name: "Vale Paços", country: "Portugal", capacity: "30 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_A },
  { name: "Portimão I", country: "Portugal", capacity: "24 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_E },
  { name: "Vale Bom", country: "Portugal", capacity: "20 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_C },
  { name: "Vista Alegre", country: "Portugal", capacity: "20 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_D },
  { name: "Alcochete", country: "Portugal", capacity: "20 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_B },
  { name: "Triana", country: "Portugal", capacity: "19 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_H },
  { name: "Almeirim", country: "Portugal", capacity: "15 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_F },
  { name: "Maceira", country: "Portugal", capacity: "14 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_G },
  { name: "Pernes", country: "Portugal", capacity: "13 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_A },
  { name: "Calhandriz", country: "Portugal", capacity: "12 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_C },
  { name: "Milagres", country: "Portugal", capacity: "8 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_E },
  { name: "Sado", country: "Portugal", capacity: "8 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_B },
  { name: "Santa Vitória", country: "Portugal", capacity: "7 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_D },
  { name: "Monte Agosto", country: "Portugal", capacity: "9 MWp", scope: ["PPC", "SCADA", "SOLAR"], img: SOLAR_IMG_H },

  // BESS
  { name: "BESS Batalha", country: "Portugal", capacity: "12 MWh", scope: ["BESS", "PPC", "SCADA"], img: BESS_IMG },

  // BMS — Gestão Técnica Centralizada (nomes bilingues: { pt, en })
  { name: { pt: "Aeroporto Internacional Amílcar Cabral", en: "Amílcar Cabral International Airport" }, location: { pt: "Ilha do Sal", en: "Sal Island" }, country: "Cape Verde", scope: ["BMS"], img: BMS_IMG_AIRPORT_A },
  { name: { pt: "Aeroporto Internacional Cesária Évora", en: "Cesária Évora International Airport" }, location: { pt: "Ilha de São Vicente", en: "São Vicente Island" }, country: "Cape Verde", scope: ["BMS"], img: BMS_IMG_AIRPORT_B },
  { name: { pt: "Aeroporto Int. de Abu Dhabi — Etihad Aircraft Maintenance", en: "Abu Dhabi Int. Airport — Etihad Aircraft Maintenance" }, location: "Abu Dhabi", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_AIRPORT_C },
  { name: { pt: "Aeroporto Internacional de Faro", en: "Faro International Airport" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_AIRPORT_A },
  { name: { pt: "Aeroporto Internacional do Dubai", en: "Dubai International Airport" }, location: "Dubai", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_AIRPORT_B },
  { name: "Autism Centre", location: "Dubai", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_OFFICE_A },
  { name: { pt: "Biblioteca Municipal de Mangualde", en: "Mangualde Municipal Library" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_LIBRARY },
  { name: { pt: "Casa Municipal da Cultura de Cantanhede", en: "Cantanhede Municipal House of Culture" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_THEATRE },
  { name: { pt: "Centro de Arte Contemporânea de Coimbra", en: "Coimbra Contemporary Art Centre" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_CIVIC },
  { name: { pt: "Centro de Saúde de Mortágua", en: "Mortágua Health Centre" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_HEALTH_A },
  { name: { pt: "Centro Escolar da Carvoeira (Caxarias)", en: "Carvoeira School Centre (Caxarias)" }, location: "Ourém", country: "Portugal", scope: ["BMS"], img: BMS_IMG_SCHOOL_A },
  { name: { pt: "Centro Escolar da Gafanha Aquém", en: "Gafanha Aquém School Centre" }, location: "Ílhavo", country: "Portugal", scope: ["BMS"], img: BMS_IMG_SCHOOL_B },
  { name: { pt: "Centro Escolar de Meinedo", en: "Meinedo School Centre" }, location: "Lousada", country: "Portugal", scope: ["BMS"], img: BMS_IMG_SCHOOL_C },
  { name: { pt: "Centro Escolar de Meixomil", en: "Meixomil School Centre" }, location: "Paços de Ferreira", country: "Portugal", scope: ["BMS"], img: BMS_IMG_SCHOOL_A },
  { name: { pt: "Centro Escolar de Quintã do Loureiro", en: "Quintã do Loureiro School Centre" }, location: "Aveiro", country: "Portugal", scope: ["BMS"], img: BMS_IMG_SCHOOL_B },
  { name: "Danat Al Emarat Hospital", location: "Abu Dhabi", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_HEALTH_B },
  { name: { pt: "Edifício Take The Wind S.A.", en: "Take The Wind S.A. Building" }, location: "Coimbra", country: "Portugal", scope: ["BMS"], img: BMS_IMG_OFFICE_B },
  { name: { pt: "Escola Sup. Agrária do Instituto Polit. de Coimbra", en: "Agrarian School of the Polytechnic Inst. of Coimbra" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_UNI_A },
  { name: { pt: "Escola Sup. Agrária do Instituto Polit. de Viseu", en: "Agrarian School of the Polytechnic Inst. of Viseu" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_UNI_B },
  { name: { pt: "Escola Sup. de Educação do Instituto Polit. de Viseu", en: "School of Education of the Polytechnic Inst. of Viseu" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_UNI_A },
  { name: { pt: "Escola Sup. de Saúde do Instituto Polit. de Viseu", en: "School of Health of the Polytechnic Inst. of Viseu" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_UNI_B },
  { name: { pt: "Escola Sup. de Tecnologia e Gestão de Lamego do Instituto Polit. de Viseu", en: "School of Technology and Management of Lamego, Polytechnic Inst. of Viseu" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_UNI_A },
  { name: { pt: "Ginásio INOVE", en: "INOVE Gym" }, location: "Porto", country: "Portugal", scope: ["BMS"], img: BMS_IMG_GYM_A },
  { name: "Hotel Carlos Alberto", location: "Porto", country: "Portugal", scope: ["BMS"], img: BMS_IMG_HOTEL_A },
  { name: { pt: "Hotel Radisson Blu", en: "Radisson Blu Hotel" }, location: "Dubai", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_HOTEL_B },
  { name: { pt: "Hotel Radisson", en: "Radisson Hotel" }, location: "Dubai", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_HOTEL_C },
  { name: { pt: "Hotel Termas da Curia", en: "Termas da Curia Hotel" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_HOTEL_D },
  { name: { pt: "Instituto Nacional de Previdência Social", en: "National Institute of Social Security" }, location: { pt: "Ilha de Santiago", en: "Santiago Island" }, country: "Cape Verde", scope: ["BMS"], img: BMS_IMG_CIVIC },
  { name: "Jebel Ali Labour Camp", location: "Dubai", country: "United Arab Emirates", scope: ["BMS"], img: BMS_IMG_CITY },
  { name: "Lingote Alumínios S.A.", location: "Fafe", country: "Portugal", scope: ["BMS"], img: BMS_IMG_INDUSTRY },
  { name: { pt: "Loja Decathlon de Coimbra", en: "Decathlon Store, Coimbra" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_OFFICE_A },
  { name: { pt: "Mercado Municipal D. Pedro V", en: "D. Pedro V Municipal Market" }, location: "Coimbra", country: "Portugal", scope: ["BMS"], img: BMS_IMG_MARKET_A },
  { name: { pt: "Mercado Municipal de Soure", en: "Soure Municipal Market" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_MARKET_B },
  { name: { pt: "Pavilhão Municipal de Mangualde", en: "Mangualde Municipal Sports Hall" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_GYM_B },
  { name: { pt: "Piscinas Municipais de Aguiar da Beira", en: "Aguiar da Beira Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_A },
  { name: { pt: "Piscinas Municipais de Anadia", en: "Anadia Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_B },
  { name: { pt: "Piscinas Municipais de Felgueiras", en: "Felgueiras Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_C },
  { name: { pt: "Piscinas Municipais de Lousada", en: "Lousada Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_D },
  { name: { pt: "Piscinas Municipais de Mira", en: "Mira Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_A },
  { name: { pt: "Piscinas Municipais de Oliveira do Bairro", en: "Oliveira do Bairro Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_B },
  { name: { pt: "Piscinas Municipais de Penela", en: "Penela Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_C },
  { name: { pt: "Piscinas Municipais de Sever do Vouga", en: "Sever do Vouga Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_D },
  { name: { pt: "Piscinas Municipais de Vila Nova de Anços", en: "Vila Nova de Anços Municipal Swimming Pools" }, country: "Portugal", scope: ["BMS"], img: BMS_IMG_POOL_A },
  { name: { pt: "Quinta da Caída Turismo Rural", en: "Quinta da Caída Rural Tourism" }, location: "Mirandela", country: "Portugal", scope: ["BMS"], img: BMS_IMG_HOTEL_E },
  { name: "Santa Luzia ArtHotel", location: "Guimarães", country: "Portugal", scope: ["BMS"], img: BMS_IMG_HOTEL_B },
];

// DICIONÁRIO DE TRADUÇÕES
const TRANSLATIONS = {
  en: {
    heroEyebrow: "Portfolio · 1 GW+ integrated",
    heroSubtitle: "More than 300 PV plants, energy storage systems, substations and buildings operating with WSBP platforms.",
    filterAll: "All",
    projectsCount: "projects",
    countries: {
      Portugal: "Portugal",
      Romania: "Romania",
      "Cape Verde": "Cape Verde",
      "United Arab Emirates": "United Arab Emirates",
    },
  },
  pt: {
    heroEyebrow: "Portfólio · Mais de 1 GW integrado",
    heroSubtitle: "Mais de 300 centrais fotovoltaicas, sistemas de armazenamento de energia, subestações e edifícios a operar com plataformas WSBP.",
    filterAll: "Todos",
    projectsCount: "projetos",
    countries: {
      Portugal: "Portugal",
      Romania: "Roménia",
      "Cape Verde": "Cabo Verde",
      "United Arab Emirates": "Emirados Árabes Unidos",
    },
  },
};

// Resolve campos bilingues ({ pt, en }) ou simples strings
function localize(value, lang) {
  if (value && typeof value === "object") return value[lang] || value.en;
  return value;
}

function ProjectCard({ p, currentLang }) {
  const translatedCountry = TRANSLATIONS[currentLang]?.countries[p.country] || p.country;
  const name = localize(p.name, currentLang);
  const location = localize(p.location, currentLang);

  return (
    <article
      data-testid={`project-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative h-[420px] overflow-hidden rounded-3xl border border-white/10 bg-black transition-all duration-500 hover:-translate-y-2 hover:border-[#F68A31]/60 hover:shadow-[0_30px_60px_-20px_rgba(246,138,49,0.45)]"
    >
      <img
        src={sized(p.img, 800)}
        alt={name}
        className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-110 group-hover:opacity-40"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/10 transition-opacity duration-500 group-hover:from-black group-hover:via-black/85" />

      <div className="relative z-10 flex h-full flex-col justify-between p-7">
        <div className="flex flex-wrap items-center gap-2">
          {p.scope.map((s) => (
            <span
              key={s}
              className="rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#F68A31]"
            >
              {s}
            </span>
          ))}
        </div>

        <div>
          <h3 className="font-display text-2xl font-bold sm:text-3xl">
            {name}
          </h3>

          {p.capacity && (
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs uppercase tracking-[0.2em] text-white/60">
              <span className="flex items-center gap-1.5 text-[#F68A31]">
                <Zap size={12} /> {p.capacity}
              </span>
            </div>
          )}

          <div
            className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-5 group-hover:grid-rows-[1fr] group-hover:opacity-100"
            data-testid={`project-${name.toLowerCase().replace(/\s+/g, "-")}-details`}
          >
            <div className="overflow-hidden">
              <div className="flex items-center gap-2 border-l-2 border-[#F68A31]/60 pl-3 text-xs uppercase tracking-[0.24em] text-white/85">
                <MapPin size={14} className="text-[#F68A31]" />
                <span className="font-semibold">
                  {location ? `${location} · ${translatedCountry}` : translatedCountry}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Projects() {
  const { language } = useLanguage();
  const [filter, setFilter] = useState("All");

  const t = TRANSLATIONS[language];

  const filterOptions = useMemo(() => [
    { id: "All", label: t.filterAll },
    { id: "PPC", label: "PPC" },
    { id: "SCADA", label: "SCADA" },
    { id: "SOLAR", label: "SOLAR" },
    { id: "BESS", label: "BESS" },
    { id: "BMS", label: "BMS & Monitoring" }
  ], [t.filterAll]);

  const filteredProjects = useMemo(() => {
    return filter === "All"
      ? PROJECTS
      : PROJECTS.filter((p) => p.scope.includes(filter));
  }, [filter]);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow={t.heroEyebrow}
        title={
          language === "pt" ? (
            <>Soluções <span className="text-[#F68A31]">WSBP</span></>
          ) : (
            <><span className="text-[#F68A31]">WSBP</span> Solutions</>
          )
        }
        subtitle={t.heroSubtitle}
        image="https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/0cbsjdbe_NtqWR.jpg"
      />

      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-8 md:px-12 lg:px-16">
        <div className="flex flex-wrap items-center gap-3" data-testid="project-filters">
          {filterOptions.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id.toLowerCase()}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
                filter === f.id
                  ? "border-[#F68A31] bg-[#F68A31] text-black"
                  : "border-white/15 bg-white/[0.03] text-white/75 hover:border-white/30"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-xs uppercase tracking-[0.28em] text-white/50">
            {filteredProjects.length} {t.projectsCount}
          </span>
        </div>

        <StaggerGroup
          key={filter}
          className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {filteredProjects.map((p) => (
            <StaggerItem key={localize(p.name, "en")}>
              <ProjectCard p={p} currentLang={language} />
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>
    </div>
  );
}